import { useEffect, useState } from 'react';
import DefaultDropDown from './DefaultDropDown';

const titles = ['Any', '1', '2', '3', '4', '5', '6', '7', '7+'];

const NumSelector = ({
    onCallback,
    selectedIndices,
    'data-cy': dataCy,
}: {
    onCallback: (indices: number[]) => void;
    selectedIndices: number[];
    'data-cy'?: string;
}) => {
    const handleClick = (index: number) => {
        if (index === 0) {
            // If clicking "Any", only select "Any"
            onCallback([0]);
        } else {
            // For other numbers, select this number and all numbers that come after it
            const newIndices = [];
            for (let i = index; i < titles.length; i++) {
                newIndices.push(i);
            }
            onCallback(newIndices);
        }
    };

    return (
        <div className="flex pb-[20px]">
            {titles.map((title, index) => {
                let borderClasses = 'border';
                let borderColor = 'border-gray-200';
                let roundedClass = '';
                let textColor = 'text-gray-600';
                let bgColor = 'bg-white';

                if (index === 0) {
                    roundedClass = 'rounded-l';
                }
                if (index === titles.length - 1) roundedClass = 'rounded-r';

                const isSelected = selectedIndices.includes(index);

                if (isSelected) {
                    borderColor = 'border-blue-500';
                    textColor = 'text-blue-500';
                    bgColor = 'bg-blue-50';
                }

                return (
                    <div
                        key={index}
                        className={`${borderClasses} ${borderColor} ${roundedClass} ${textColor} ${bgColor} text-center cursor-pointer hover:bg-gray-50 w-[36px] h-[36px] leading-[36px] transition-colors duration-200`}
                        onClick={() => handleClick(index)}
                        data-cy={dataCy}
                    >
                        {title}
                    </div>
                );
            })}
        </div>
    );
};

const BedBathFilter = ({
    onCallback,
    bedIndices,
    bathIndices,
}: {
    onCallback: (beds: number[], baths: number[]) => void;
    className?: string;
    bedIndices: number[];
    bathIndices: number[];
}) => {
    const [selectedBedIndices, setSelectedBedIndices] = useState<number[]>(bedIndices || [0]);
    const [selectedBathIndices, setSelectedBathIndices] = useState<number[]>(bathIndices || [0]);
    const [title, setTitle] = useState('');

    useEffect(() => {
        setSelectedBedIndices(bedIndices || [0]);
    }, [bedIndices]);

    useEffect(() => {
        setSelectedBathIndices(bathIndices || [0]);
    }, [bathIndices]);

    useEffect(() => {
        const bedText = selectedBedIndices.includes(0) ? 'Any' : selectedBedIndices.sort((a, b) => a - b).join(',');
        const bathText = selectedBathIndices.includes(0) ? 'Any' : selectedBathIndices.sort((a, b) => a - b).join(',');

        setTitle(`${bedText} Bed, ${bathText} Bath`);
    }, [selectedBedIndices, selectedBathIndices]);

    const onClearClicked = () => {
        setSelectedBathIndices([0]);
        setSelectedBedIndices([0]);
        onCallback([0], [0]);
    };

    return (
        <DefaultDropDown Title={title} IsEmpty={selectedBedIndices.includes(0) && selectedBathIndices.includes(0)} onClearClicked={onClearClicked}>
            <div className="px-[15px] w-fit flex flex-col gap-[12px]">
                <div className="h-[40px] leading-[40px]">Number of Bedrooms</div>
                <NumSelector
                    onCallback={(newBedIndices) => {
                        setSelectedBedIndices(newBedIndices);
                        onCallback(newBedIndices, selectedBathIndices);
                    }}
                    selectedIndices={selectedBedIndices}
                    data-cy="bed-filter"
                />
                <div className="h-[40px] leading-[40px]">Number of Bathrooms</div>
                <NumSelector
                    onCallback={(newBathIndices) => {
                        setSelectedBathIndices(newBathIndices);
                        onCallback(selectedBedIndices, newBathIndices);
                    }}
                    selectedIndices={selectedBathIndices}
                    data-cy="bath-filter"
                />
            </div>
        </DefaultDropDown>
    );
};

export default BedBathFilter;
